// src/pages/QuestionnairePage/steps/Step1Age.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { User, Calendar } from 'lucide-react';

const StepCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 24px;
  padding: 32px 20px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  @media (max-width: 375px) {
    padding: 28px 16px;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0);
    }
  }
`;

const WelcomeText = styled.p`
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.5;
  padding: 12px;
  background: var(--surface-medium);
  border-radius: 8px;
  border-left: 3px solid #7B68EE;

  [data-theme="dark"] & {
    border-left-color: #C4B5FD;
  }
`;

const Question = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 20px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #7B68EE;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    svg {
      color: #C4B5FD;
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 16px 56px 16px 20px !important;
  border: 2px solid rgba(123, 104, 238, 0.2) !important;
  border-radius: 16px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #000000 !important;
  background: rgba(255, 255, 255, 0.92) !important;
  transition: all 0.3s ease !important;
  outline: none !important;
  box-shadow: 0 2px 10px rgba(123, 104, 238, 0.12);

  &::placeholder {
    color: rgba(0,0,0,0.4) !important;
    font-weight: 500 !important;
  }

  &:focus {
    border-color: #7B68EE !important;
    background: #FFFFFF !important;
    box-shadow: 0 4px 18px rgba(123, 104, 238, 0.25);
    transform: translateY(-1px);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }

  [data-theme="dark"] & {
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border-color: rgba(157, 142, 238, 0.3) !important;

    &::placeholder {
      color: rgba(255,255,255,0.4) !important;
    }

    &:focus {
      border-color: #C4B5FD !important;
      background: rgba(255, 255, 255, 0.12) !important;
      box-shadow: 0 4px 18px rgba(157, 142, 238, 0.3);
    }
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7B68EE;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

interface Step1AgeProps {
  value: number | null;
  onChange: (age: number | null) => void;
}

export const Step1Age: React.FC<Step1AgeProps> = ({ value, onChange }) => {
  return (
    <StepCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <WelcomeText>
        Чтобы мои подсказки были максимально точными и комфортными именно для Вашей кожи, задам несколько вопросов 😊
      </WelcomeText>

      <Question>
        <User size={24} />
        Сколько Вам лет?
      </Question>
      
      <InputWrapper>
        <StyledInput
          type="number"
          min={1}
          max={120}
          placeholder="Например, 28"
          value={value ?? ''}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val > 0) {
              onChange(val);
              return;
            }

            if (e.target.value === '') {
              onChange(null);
            }
          }}
        />
        <InputIcon>
          <Calendar size={20} />
        </InputIcon>
      </InputWrapper>
    </StepCard>
  );
};